import type { Dispatch } from 'redux';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';

import {
  getUserRoleIsProsecutorOrSuperProsecutor,
  getUserRoleIsSupervisor,
} from 'AuthPackage/MainModule/selectors/authSelectors';
import { getIsSticked } from 'CorePackage/HeaderModule/reducers/headerReducer';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { Button } from 'UIPackage/MainModule/components/Button';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { ConfirmModal } from 'UIPackage/MainModule/components/ConfirmModal';
import { IDecision } from 'APIPackage/DecisionModule/interfaces/IDecision';
import {
  getDecisionData,
  getDecisionMassCancelBlock,
  getDecisionFormRef,
  getDecisionOnSaveButtonClick,
  getDecisionWatchOnlyByTestUser,
  getDecisionIsPrescriptionOldRules,
  getDecisionIsPrescriptionFailed,
} from 'DecisionPackage/MainModule/selectors/decisionSelectors';
import { decisionDelete, decisionTryOpenSignModal } from 'DecisionPackage/MainModule/actions/decisionActions';
import styles from 'KnmPackage/KnmHeaderModule/components/KnmHeaderButtons.module.scss';
import { defineIsActive } from 'KnmPackage/KnmHeaderModule/common/defineIsActive';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { IDecisionOrganizationForm } from 'DecisionPackage/MainModule/interfaces/IDecisionOrganizationForm';
import { KindResultDecisionCodesEnum } from 'APIPackage/CatalogsModule/enums/KindResultDecisionCodesEnum';
import { KnmErknmStatusCodesEnum } from 'APIPackage/CatalogsModule/enums/KnmErknmStatusCodesEnum';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { fetchExcludedViolationsFromReinspection } from 'APIPackage/DecisionModule/services/decisionApi';
import { NotificationRepeatViolationModal } from 'KnmPackage/KnmHeaderModule/components/NotifcationRepeatViolationModal/NotificationRepeatViolationModal';
import { IKnmErknmFormViolatedRequirementsNpa } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormViolatedRequirementsNpa';
import { convertIKnmErknmFormViolatedRequirementsNpaToIKnmErknmFormRequirementNpa } from 'KnmErknmPackage/MainModule/common/convertIKnmErknmFormViolatedRequirementsNpaToIKnmErknmFormRequirementNpa';
import { convertKnmDataRequirementsNpaToFormValuesDecision } from 'KnmErknmPackage/MainModule/common/convertKnmDataRequirementsNpaToFormValuesDecision';
import { KnmReasonTypeNameEnum } from 'KnmPackage/MainModule/enums/KnmReasonTypeNameEnum';

import { DecisionHeaderDropdown } from './DecisionHeaderDropdown';
import { useFormFieldWatch } from '../../../hooks/useFormFieldWatch';
import { IBooleanSelectOption } from '../../../KnmPackage/MainModule/interfaces/IBooleanSelectOption';

interface IDecisionHeaderButtonsProps {
  className?: string;
  isSelfHidden?: true;
  location: 'header' | 'form';
  handleMouseOverDecisionSignButton?: () => void;
  handleMouseOutDecisionSignButton?: () => void;
}

interface IDecisionHeaderButtonsStoreProps {
  isHeaderSticked: boolean;
  decisionData: IDecision | undefined;
  onSaveButtonClick: (() => void) | undefined;
  isProsecutorOrSuperProsecutor: boolean | undefined;
  isSupervisor: boolean | undefined;
  isDecisionWatchOnlyByTestUser: boolean;
  isMassCancelBlock: boolean;
  settings: IAdminSetting[];
  decisionFormRef: IDecisionFormRef | undefined;
}

const DecisionHeaderButtonsWithoutStore = ({
  className,
  isSelfHidden,
  location,
  isHeaderSticked,
  decisionData,
  onSaveButtonClick,
  isProsecutorOrSuperProsecutor,
  isSupervisor,
  isDecisionWatchOnlyByTestUser,
  isMassCancelBlock,
  settings,
  decisionFormRef,
  handleMouseOutDecisionSignButton,
  handleMouseOverDecisionSignButton,
}: IDecisionHeaderButtonsProps & IDecisionHeaderButtonsStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const [isRequirementsNpaModalVisible, setIsRequirementsNpaModalVisible] = useState(false);
  const [willBeRemoveRequirements, setWillBeRemoveRequirements] = useState<IKnmErknmFormViolatedRequirementsNpa[]>([]);
  const [isActive, setIsActive] = useState(defineIsActive(location, isHeaderSticked));
  const requirementsCurrent = convertIKnmErknmFormViolatedRequirementsNpaToIKnmErknmFormRequirementNpa(
    // @ts-ignore
    decisionFormRef?.current.getFieldState('violatedMandatoryRequirements')?.value ||
      // @ts-ignore
      decisionFormRef?.current.getFieldState('organization.act.violatedMandatoryRequirements')?.value,
  );
  const organization = useFormFieldWatch(decisionFormRef?.current, 'organization', '') as
    | IDecisionOrganizationForm
    | undefined;

  const isEnablePrescriptionCheck = isLaterThanAdminSetting(
    decisionData?.createdAt,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_105_166_25_4_1_30_STARTS_FROM,
  );

  const isNeedToAddAct = useFormFieldWatch(decisionFormRef?.current, 'isNeedToAddAct') as
    | IBooleanSelectOption
    | undefined;

  const isEnablePaperless = settings?.find(
    setting => setting.code === AdminSettingCodesEnum.ENABLED_PAPERLESS_PASSPORT_ACT_PRESCRIPTION,
  );

  useEffect(() => {
    setIsActive(defineIsActive(location, isHeaderSticked));
  }, [location, isHeaderSticked]);

  const headerAttributes = {
    ...(!isActive && isSelfHidden && { 'data-hidden': true }),
  };

  const [isConfirmDeleteModalVisible, setIsConfirmDeleteModalVisible] = useState(false);
  const handleDeleteDecision = () => setIsConfirmDeleteModalVisible(true);

  const handleDeleteClick = useCallback(() => {
    setIsConfirmDeleteModalVisible(true);
    dispatch(decisionDelete());
  }, [dispatch]);

  const handleDeleteModalClose = useCallback(() => setIsConfirmDeleteModalVisible(false), []);

  const actGuid = useMemo(() => {
    return organization?.act?.guid || undefined;
  }, [organization]);

  const handleOpenSignModalOrTestSign = useCallback(() => {
    dispatch(decisionTryOpenSignModal(actGuid));
  }, [actGuid]);

  const isPrescriptionCheckByOldRules = useSelector(getDecisionIsPrescriptionOldRules);
  const isPassportByCheckPrescriptionFailed = useSelector(getDecisionIsPrescriptionFailed);

  /**
   * Валидация неактивных нарушений при повторной проверке
   * если требование выведено в прод и у паспорта основание 'Проверка предписания'
   */
  const validateNotActiveViolations = useCallback(() => {
    const isPrescriptionCheck =
      decisionData?.reasons?.[0].reason?.type?.name === KnmReasonTypeNameEnum.PRESCRIPTION_CHECK &&
      isPrescriptionCheckByOldRules === false;

    if (
      isEnablePrescriptionCheck &&
      (isPrescriptionCheck || isPassportByCheckPrescriptionFailed === true) &&
      decisionData
    ) {
      return fetchExcludedViolationsFromReinspection(decisionData.id).then(requirements => {
        if (requirements?.length) {
          // Показываем модальное окно с предупреждением
          setIsRequirementsNpaModalVisible(true);
          setWillBeRemoveRequirements(convertKnmDataRequirementsNpaToFormValuesDecision(requirements) || []);

          return false;
        }

        return true;
      });
    }

    return new Promise<boolean>(resolve => resolve(true));
  }, [
    decisionData,
    isEnablePrescriptionCheck,
    decisionFormRef,
    isPrescriptionCheckByOldRules,
    isPassportByCheckPrescriptionFailed,
  ]);

  /**
   * Обработка нажатия кнопки "Подписать паспорт"
   */
  const onClickButtonSign = useCallback(async () => {
    if (decisionData?.status?.code === KnmErknmStatusCodesEnum.FILL_IN_PROCESS) {
      const isValid = await validateNotActiveViolations();

      if (!isValid) return null;
    }

    handleOpenSignModalOrTestSign();
  }, [handleOpenSignModalOrTestSign, validateNotActiveViolations, decisionData]);

  const hasUnsignedResultDecision = useMemo(() => {
    return !!organization?.resultDecisions?.find(
      rd =>
        (rd.kind?.code === KindResultDecisionCodesEnum.D_1 || rd.kind?.code === KindResultDecisionCodesEnum.D_7) &&
        rd.paperless &&
        !rd.signed,
    );
  }, [organization]);

  const isShowSignButton = useMemo(() => {
    return (
      isSupervisor &&
      (!decisionData?.signed || decisionData.resigningRequired) &&
      !isDecisionWatchOnlyByTestUser &&
      !decisionData?.deleted &&
      (isEnablePaperless?.value === 'false' ||
        ((organization?.act
          ? !organization?.act.paperless || organization?.act.signed || isNeedToAddAct?.value
          : true) &&
          !hasUnsignedResultDecision))
    );
  }, [organization, decisionFormRef?.current, decisionData, isEnablePaperless, isNeedToAddAct]);

  const isDisabledSignButton = useMemo(() => {
    // Блокируем кнопку подписания, если
    // 1. В ПР выбрано поле "Будет ли добавлен акт - Да"
    // 2. АКТ - не добавлен / добавлен новый (но еще не сохранен)

    const isDirty = decisionFormRef?.current?.getState()?.dirty;

    return isNeedToAddAct?.value && (!organization?.act || organization?.act?.isNew || isDirty);
  }, [organization, isNeedToAddAct, decisionFormRef?.current]);

  const handleSignButtonHover = useCallback(
    (isHover: boolean) => {
      if (isHover && !isDisabledSignButton) {
        handleMouseOverDecisionSignButton?.();
      } else {
        handleMouseOutDecisionSignButton?.();
      }
    },
    [handleMouseOverDecisionSignButton, handleMouseOutDecisionSignButton, isDisabledSignButton],
  );

  return (
    <div className={`${styles.Container} ${className}`} {...headerAttributes}>
      {isShowSignButton && (
        <Button
          className={styles.SignButtonDecision}
          text="Подписать паспорт"
          id="signButton"
          type="button"
          disabled={isDisabledSignButton}
          onClick={onClickButtonSign}
          onMouseOver={() => handleSignButtonHover(true)}
          onMouseOut={() => handleSignButtonHover(false)}
        />
      )}
      {(isProsecutorOrSuperProsecutor || isSupervisor) && !isDecisionWatchOnlyByTestUser && (
        <Button
          color="primary"
          text="Сохранить"
          id="saveButton"
          type={onSaveButtonClick ? 'button' : 'submit'}
          disabled={decisionData?.deleted || isMassCancelBlock}
          onClick={onSaveButtonClick}
        />
      )}
      {!isDecisionWatchOnlyByTestUser && (
        <DecisionHeaderDropdown
          location={location}
          handleDeleteDecision={handleDeleteDecision}
          onHandleSign={onClickButtonSign}
        />
      )}
      <ConfirmModal
        visible={isConfirmDeleteModalVisible}
        title="Подтверждение"
        text="Удалить Решение?"
        applyButtonText="Удалить"
        cancelButtonText="Отменить"
        onApply={handleDeleteClick}
        onClose={handleDeleteModalClose}
      />
      <NotificationRepeatViolationModal
        requirementsCurrent={requirementsCurrent}
        requirementsNpa={willBeRemoveRequirements}
        visible={isRequirementsNpaModalVisible}
        onClose={() => setIsRequirementsNpaModalVisible(false)}
        onSubmit={handleOpenSignModalOrTestSign}
      />
    </div>
  );
};

const mapStateToProps = (state: IRootState): IDecisionHeaderButtonsStoreProps => ({
  isHeaderSticked: getIsSticked(state),
  decisionData: getDecisionData(state),
  onSaveButtonClick: getDecisionOnSaveButtonClick(state),
  isProsecutorOrSuperProsecutor: getUserRoleIsProsecutorOrSuperProsecutor(state),
  isSupervisor: getUserRoleIsSupervisor(state),
  isDecisionWatchOnlyByTestUser: getDecisionWatchOnlyByTestUser(state),
  isMassCancelBlock: getDecisionMassCancelBlock(state),
  settings: getAdminFetchSettings(state),
  decisionFormRef: getDecisionFormRef(state),
});

export const DecisionHeaderButtons = connect(mapStateToProps)(DecisionHeaderButtonsWithoutStore);
